import { useState } from 'react'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import {
  HouseCorporateEveningIndividualFormContainer,
  HouseCorporateEveningIndividualFormContainerProps,
} from './components/form-container/HouseCorporateEveningIndividualFormContainer'
import { HouseCorporateEveningIndividualIsEnabledField } from './components/is-enabled-field/HouseCorporateEveningIndividualIsEnabledField'
import { HouseCorporateEveningIndividualOptionsFields } from './components/options-fields/HouseCorporateEveningIndividualOptionsFields'
import {
  computeHouseCorporateEveningIndividualDefaultValues,
  computeHouseCorporateEveningIndividualInputs,
} from './helpers/compute-house-corporate-evening-individual'
import { HouseCorporateEveningIndividualDto } from '../../../../../../lib/graphql'
import { HouseOptionsFormLayoutProps } from '../../../HouseOptionsForm'

export type HouseCorporateEveningIndividualFormProps = {
  updateCorporateEveningIndividual: {
    mutate: (formData: HouseCorporateEveningIndividualDto) => void
    isLoading: boolean
  }
}
export const HouseCorporateEveningIndividualForm = ({
  handleOnExpand,
  house,
  isDisabled,
  isOpen,
  layoutProps,
  updateCorporateEveningIndividual,
}: HouseCorporateEveningIndividualFormProps & HouseOptionsFormLayoutProps) => {
  const [hasRequestBeenSent, setHasRequestBeenSent] = useState<boolean>(false)

  const { currencyArea, options } = house

  // Infer GQL Union type
  if (options.__typename !== 'HouseOptionsIndividual') {
    return null
  }

  const { corporateEvening } = options

  const methods = useForm<HouseCorporateEveningIndividualDto>({
    defaultValues:
      computeHouseCorporateEveningIndividualDefaultValues(corporateEvening),
  })

  const {
    control,
    watch,
    reset,
    formState: { isSubmitting, isValid, isDirty },
    handleSubmit,
  } = methods

  const { mutate } = updateCorporateEveningIndividual

  const isEnabled = watch('isEnabled')

  const timeOutRequest = () => {
    setHasRequestBeenSent(true)
    setTimeout(() => setHasRequestBeenSent(false), 2000)
  }

  const onSubmit = (formData: HouseCorporateEveningIndividualDto) => {
    const inputs = computeHouseCorporateEveningIndividualInputs({ ...formData })

    mutate({ ...inputs })

    reset(formData)

    timeOutRequest()
  }

  const isFieldDisabled = isDisabled || hasRequestBeenSent

  const formContainerProps: Omit<
    HouseCorporateEveningIndividualFormContainerProps,
    'children'
  > = {
    handleOnExpand,
    isOpen,
    layoutProps,
    submitButtons: {
      confirm: hasRequestBeenSent,
      disabled: isFieldDisabled || !isDirty || !isValid,
      isLoading: isSubmitting || updateCorporateEveningIndividual.isLoading,
      onSubmit: handleSubmit(onSubmit),
      isValid,
    },
  }

  return (
    <FormProvider {...methods}>
      <HouseCorporateEveningIndividualFormContainer {...formContainerProps}>
        <HouseCorporateEveningIndividualIsEnabledField
          control={control}
          isFieldDisabled={isFieldDisabled}
        />

        {isEnabled ? (
          <HouseCorporateEveningIndividualOptionsFields
            control={control}
            currencyArea={currencyArea}
            isFieldDisabled={isFieldDisabled}
          />
        ) : null}
      </HouseCorporateEveningIndividualFormContainer>
    </FormProvider>
  )
}
